
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  Users, BookOpen, DollarSign, TrendingUp, Activity,
  Search, Shield, LogOut, BarChart3, Heart, MessageCircle,
  UserX, Trash2, Eye, Star, Plus, RefreshCw, ChevronLeft,
  ChevronRight, Ban, CheckCircle, Sparkles, Clock, XCircle, X,
  LayoutDashboard, MessageSquare, Settings, MoreVertical, CreditCard, Link,
  Palette, Database, Target, Image, ShoppingBag, EyeOff, Edit2, Package,
  IndianRupee, ToggleLeft, ToggleRight, UserCheck, FileCheck, Banknote,
  AlertCircle, Copy, Send, ExternalLink, Wallet, Tag, Save
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Admin Layout wrapper
const AdminLayout = ({ children, activeTab, setActiveTab, onLogout }) => {
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'comics', label: 'Comics', icon: BookOpen },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'posts', label: 'Posts', icon: MessageCircle },
    { id: 'credits', label: 'Credit Revenue', icon: Sparkles },
    { id: 'revenue', label: 'Tips Revenue', icon: DollarSign },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'store', label: 'Store', icon: ShoppingBag },
    { id: 'feature-toggles', label: 'Feature Toggles', icon: Settings },
    { id: 'user-submissions', label: 'User Submissions', icon: FileCheck },
    { id: 'creator-earnings', label: 'Creator Earnings', icon: IndianRupee },
    { id: 'payouts', label: 'Payouts', icon: Banknote },
    { id: 'cavaggio', label: 'CARAVAGGIO', icon: Palette },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-gray-900 border-r border-gray-800 p-4 z-50">
        <div className="flex items-center gap-3 mb-8 px-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-500 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">ComicMenia</h1>
            <p className="text-xs text-gray-500">Admin Panel</p>
          </div>
        </div>

        <nav className="space-y-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id
                ? 'bg-purple-600/20 text-purple-400 border border-purple-500/30'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-gray-400 hover:text-red-400 hover:bg-red-500/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-6">
        {children}
      </main>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, change, icon: Icon, color, subtitle }) => (
  <Card className="bg-gray-900 border-gray-800">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-400 mb-1">{title}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          {change !== undefined && (
            <p className={`text-sm mt-2 flex items-center gap-1 ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              <TrendingUp className={`w-4 h-4 ${change < 0 ? 'rotate-180' : ''}`} />
              {change >= 0 ? '+' : ''}{change} this week
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Overview Tab
const OverviewTab = ({ stats, growthData, activity, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard Overview</h1>
          <p className="text-gray-400">Real-time platform statistics</p>
        </div>
        <Button onClick={onRefresh} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Users"
          value={stats?.users?.total || 0}
          change={stats?.users?.new_this_week}
          icon={Users}
          color="bg-purple-600"
          subtitle={`${stats?.users?.blocked || 0} blocked`}
        />
        <StatsCard
          title="Total Comics"
          value={stats?.comics?.total || 0}
          change={stats?.comics?.new_this_week}
          icon={BookOpen}
          color="bg-cyan-600"
        />
        <StatsCard
          title="Platform Revenue"
          value={`$${(stats?.revenue?.platform_fees || 0).toFixed(2)} `}
          change={stats?.revenue?.transactions_this_week}
          icon={DollarSign}
          color="bg-green-600"
          subtitle={`${stats?.revenue?.total_transactions || 0} transactions`}
        />
        <StatsCard
          title="Engagement"
          value={stats?.engagement?.total_likes || 0}
          change={stats?.engagement?.likes_this_week}
          icon={Heart}
          color="bg-pink-600"
          subtitle={`${stats?.engagement?.total_comments || 0} comments`}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Growth Chart */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Growth Trends</CardTitle>
            <CardDescription className="text-gray-400">Users and comics over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => v.slice(5)} />
                  <YAxis stroke="#9CA3AF" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#9CA3AF' }}
                  />
                  <Area type="monotone" dataKey="users" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.3} name="New Users" />
                  <Area type="monotone" dataKey="comics" stackId="2" stroke="#06B6D4" fill="#06B6D4" fillOpacity={0.3} name="New Comics" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Chart */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Revenue</CardTitle>
            <CardDescription className="text-gray-400">Platform fees collected</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => v.slice(5)} />
                  <YAxis stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => `$${v} `} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#9CA3AF' }}
                    formatter={(value) => [`$${value.toFixed(2)} `, 'Revenue']}
                  />
                  <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Creators */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Top Creators
            </CardTitle>
            <CardDescription className="text-gray-400">By tips received</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.top_creators?.length > 0 ? (
                stats.top_creators.map((creator, idx) => (
                  <div key={creator.user_id} className="flex items-center justify-between p-3 rounded-lg bg-gray-800">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-gray-500">#{idx + 1}</span>
                      <div>
                        <p className="text-white font-medium">{creator.display_name || creator.username}</p>
                        <p className="text-xs text-gray-400">{creator.tips_count} tips</p>
                      </div>
                    </div>
                    <p className="text-green-400 font-bold">${creator.total_tips.toFixed(2)}</p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No tip data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-500" />
              Recent Activity
            </CardTitle>
            <CardDescription className="text-gray-400">Latest platform events</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {activity?.length > 0 ? (
                activity.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-2">
                    <div className={`p-2 rounded-lg ${item.type === 'user_registration' ? 'bg-purple-500/20 text-purple-400' :
                      item.type === 'comic_created' ? 'bg-cyan-500/20 text-cyan-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                      {item.type === 'user_registration' ? <Users className="w-4 h-4" /> :
                        item.type === 'comic_created' ? <BookOpen className="w-4 h-4" /> :
                          <DollarSign className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{item.message}</p>
                      <p className="text-xs text-gray-500">
                        {item.timestamp ? new Date(item.timestamp).toLocaleString() : 'Just now'}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No recent activity</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Credits & Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-yellow-500/20">
                <Sparkles className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">AI Credits Used</p>
                <p className="text-2xl font-bold text-white">{stats?.ai_credits?.total_used || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-500/20">
                <MessageCircle className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Comments This Week</p>
                <p className="text-2xl font-bold text-white">{stats?.engagement?.comments_this_week || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-orange-500/20">
                <Clock className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">New Users (30 days)</p>
                <p className="text-2xl font-bold text-white">{stats?.users?.new_this_month || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Users Tab
const UsersTab = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const limit = 10;


  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit, search: search || undefined };
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Fetch users error:', err);
      toast.error(err.response?.data?.detail || err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [token, page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleBlockUser = async (userId) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/admin/users/${userId}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const [creditModal, setCreditModal] = useState({ open: false, user: null });
  const [creditForm, setCreditForm] = useState({ amount: 50, reason: '', package_type: 'manual' });

  const openCreditModal = (user) => {
    setCreditModal({ open: true, user });
    setCreditForm({ amount: 50, reason: '', package_type: 'manual' });
  };

  const handleAddCredits = async () => {
    if (!creditModal.user || creditForm.amount < 1) return;

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/credits/add`, {
        user_id: creditModal.user.id,
        amount: creditForm.amount,
        reason: creditForm.reason,
        package_type: creditForm.package_type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Added ${creditForm.amount} credits to ${response.data.user}. New total: ${response.data.total_credits}`);
      setCreditModal({ open: false, user: null });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add credits');
    }
  };

  const creditPackages = [
    { label: '10 Credits', value: 10 },
    { label: '25 Credits', value: 25 },
    { label: '50 Credits', value: 50 },
    { label: '100 Credits', value: 100 },
    { label: '250 Credits', value: 250 },
    { label: '500 Credits', value: 500 },
  ];

  const creditReasons = [
    { label: 'Manual Addition', value: 'manual' },
    { label: 'Gift / Promotion', value: 'gift' },
    { label: 'Refund', value: 'refund' },
    { label: 'Compensation', value: 'compensation' },
    { label: 'Contest Winner', value: 'contest' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <p className="text-gray-400">{total} total users</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="pl-10 bg-gray-800 border-gray-700 text-white w-64"
            />
          </div>
          <Button onClick={fetchUsers} variant="outline" className="border-gray-700 text-gray-300">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-4 text-gray-400 font-medium">User</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Email</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Comics</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Likes</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Status</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Joined</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-gray-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-gray-500">No users found</td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-cyan-500 flex items-center justify-center text-white font-bold">
                            {(user.display_name || user.username)?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.display_name || user.username}</p>
                            <p className="text-sm text-gray-500">@{user.username}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-gray-300">{user.email}</td>
                      <td className="p-4 text-center text-gray-300">{user.stats?.comics_count || 0}</td>
                      <td className="p-4 text-center text-gray-300">{user.stats?.total_likes || 0}</td>
                      <td className="p-4 text-center">
                        {user.is_blocked ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">Blocked</span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">Active</span>
                        )}
                      </td>
                      <td className="p-4 text-center text-gray-400 text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedUser(user)}
                            className="text-gray-400 hover:text-white"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openCreditModal(user)}
                            className="text-yellow-400 hover:text-yellow-300"
                            title="Add Credits"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleBlockUser(user.id)}
                            className={user.is_blocked ? 'text-green-400 hover:text-green-300' : 'text-orange-400 hover:text-orange-300'}
                            title={user.is_blocked ? 'Unblock' : 'Block'}
                          >
                            {user.is_blocked ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteUser(user.id, user.username)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-gray-800">
            <p className="text-sm text-gray-400">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-gray-700 text-gray-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                className="border-gray-700 text-gray-300"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSelectedUser(null)}>
          <Card className="bg-gray-900 border-gray-800 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-cyan-500 flex items-center justify-center text-white font-bold text-xl">
                  {(selectedUser.display_name || selectedUser.username)?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p>{selectedUser.display_name || selectedUser.username}</p>
                  <p className="text-sm text-gray-400 font-normal">@{selectedUser.username}</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-gray-800">
                  <p className="text-gray-400 text-sm">Email</p>
                  <p className="text-white">{selectedUser.email}</p>
                </div>
                <div className="p-3 rounded-lg bg-gray-800">
                  <p className="text-gray-400 text-sm">Status</p>
                  <p className={selectedUser.is_blocked ? 'text-red-400' : 'text-green-400'}>
                    {selectedUser.is_blocked ? 'Blocked' : 'Active'}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-gray-800">
                  <p className="text-gray-400 text-sm">Comics</p>
                  <p className="text-white">{selectedUser.stats?.comics_count || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-gray-800">
                  <p className="text-gray-400 text-sm">Total Likes</p>
                  <p className="text-white">{selectedUser.stats?.total_likes || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-gray-800">
                  <p className="text-gray-400 text-sm">Tips Received</p>
                  <p className="text-white">{selectedUser.stats?.tips_received || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-gray-800">
                  <p className="text-gray-400 text-sm">Joined</p>
                  <p className="text-white">
                    {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
              {selectedUser.bio && (
                <div className="p-3 rounded-lg bg-gray-800">
                  <p className="text-gray-400 text-sm mb-1">Bio</p>
                  <p className="text-white">{selectedUser.bio}</p>
                </div>
              )}
              <Button onClick={() => setSelectedUser(null)} className="w-full bg-gray-800 hover:bg-gray-700 text-white">
                Close
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Credits Modal */}
      {creditModal.open && creditModal.user && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setCreditModal({ open: false, user: null })}>
          <Card className="bg-gray-900 border-gray-800 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Plus className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p>Add Credits</p>
                  <p className="text-sm text-gray-400 font-normal">to @{creditModal.user.username}</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Quick Amount Buttons */}
              <div>
                <p className="text-gray-400 text-sm mb-2">Quick Select</p>
                <div className="grid grid-cols-3 gap-2">
                  {creditPackages.map(pkg => (
                    <Button
                      key={pkg.value}
                      size="sm"
                      variant={creditForm.amount === pkg.value ? 'default' : 'outline'}
                      onClick={() => setCreditForm({ ...creditForm, amount: pkg.value })}
                      className={creditForm.amount === pkg.value
                        ? 'bg-yellow-500 hover:bg-yellow-600 text-black'
                        : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
                    >
                      {pkg.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div>
                <p className="text-gray-400 text-sm mb-2">Custom Amount</p>
                <Input
                  type="number"
                  value={creditForm.amount}
                  onChange={(e) => setCreditForm({ ...creditForm, amount: parseInt(e.target.value) || 0 })}
                  className="bg-gray-800 border-gray-700 text-white"
                  min="1"
                  max="10000"
                />
              </div>

              {/* Reason Selection */}
              <div>
                <p className="text-gray-400 text-sm mb-2">Reason</p>
                <div className="grid grid-cols-2 gap-2">
                  {creditReasons.map(reason => (
                    <Button
                      key={reason.value}
                      size="sm"
                      variant={creditForm.package_type === reason.value ? 'default' : 'outline'}
                      onClick={() => setCreditForm({ ...creditForm, package_type: reason.value })}
                      className={creditForm.package_type === reason.value
                        ? 'bg-purple-600 hover:bg-purple-700'
                        : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
                    >
                      {reason.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div>
                <p className="text-gray-400 text-sm mb-2">Note (optional)</p>
                <Input
                  type="text"
                  value={creditForm.reason}
                  onChange={(e) => setCreditForm({ ...creditForm, reason: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Add a note for the audit log..."
                />
              </div>

              {/* Summary */}
              <div className="p-4 rounded-lg bg-gray-800 border border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Credits to add:</span>
                  <span className="text-2xl font-bold text-yellow-400">{creditForm.amount}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => setCreditModal({ open: false, user: null })}
                  variant="outline"
                  className="flex-1 border-gray-700 text-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddCredits}
                  disabled={creditForm.amount < 1}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                >
                  Add {creditForm.amount} Credits
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Comics Tab
const ComicsTab = ({ token }) => {
  const [comics, setComics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 10;
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategories, setEditCategories] = useState([]);
  const [editTags, setEditTags] = useState([]);
  const [newCatInput, setNewCatInput] = useState('');
  const [newTagInput, setNewTagInput] = useState('');

  const PREDEFINED_CATEGORIES = [
    'Action', 'Comedy', 'Drama', 'Romance', 'Dark Fantasy',
    'Psych Thriller', 'Sci-Fi', 'Horror', 'Slice of Life',
    'Mystery', 'Adventure', 'Superhero', 'AI Comics', 'Manga',
    'Webtoon', 'Historical', 'Sports', 'Music', 'Fantasy'
  ];

  const PREDEFINED_TAGS = [
    'Ongoing', 'Completed', 'Short Story', 'One-Shot', 'Full Color',
    'Black & White', 'Mature', 'All Ages', 'AI Generated',
    'Hand Drawn', 'Digital Art', 'Indie', 'Debut', 'Fan Art',
    'Original', 'Adaptation', 'Collaboration', 'Featured Artist'
  ];

  const fetchComics = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit, search: search || undefined };
      const response = await axios.get(`${BACKEND_URL}/api/admin/comics`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setComics(response.data.comics);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Fetch comics error:', err);
      toast.error(err.response?.data?.detail || err.message || 'Failed to fetch comics');
    } finally {
      setLoading(false);
    }
  }, [token, page, search]);

  useEffect(() => {
    fetchComics();
  }, [fetchComics]);

  const handleDeleteComic = async (comicId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/comics/${comicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Comic deleted successfully');
      fetchComics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete comic');
    }
  };

  const handleFeatureComic = async (comicId) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/admin/comics/${comicId}/feature`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchComics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update comic');
    }
  };

  const handleToggleTips = async (comicId) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/admin/comics/${comicId}/toggle-tips`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setComics(comics.map(comic =>
        comic.id === comicId
          ? { ...comic, tips_enabled: response.data.tips_enabled }
          : comic
      ));

      toast.success(response.data.tips_enabled ? "Tips enabled for comic" : "Tips disabled for comic");
    } catch (error) {
      console.error("Toggle tips error:", error);
      toast.error("Failed to toggle tips status");
    }
  };

  const startEditCategories = (comic) => {
    setEditingCategoryId(comic.id);
    setEditCategories(comic.categories || []);
    setEditTags(comic.tags || []);
    setNewCatInput('');
    setNewTagInput('');
  };

  const cancelEditCategories = () => {
    setEditingCategoryId(null);
    setEditCategories([]);
    setEditTags([]);
    setNewCatInput('');
    setNewTagInput('');
  };

  const handleSaveCategories = async (comicId) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/comics/${comicId}/categories`,
        { categories: editCategories, tags: editTags },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Categories updated');
      setComics(comics.map(c =>
        c.id === comicId ? { ...c, categories: editCategories, tags: editTags } : c
      ));
      setEditingCategoryId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update categories');
    }
  };

  const toggleEditCategory = (cat) => {
    setEditCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const toggleEditTag = (tag) => {
    setEditTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const addCustomCategory = () => {
    const cat = newCatInput.trim();
    if (cat && !editCategories.includes(cat)) {
      setEditCategories(prev => [...prev, cat]);
      setNewCatInput('');
    }
  };

  const addCustomTag = () => {
    const tag = newTagInput.trim();
    if (tag && !editTags.includes(tag)) {
      setEditTags(prev => [...prev, tag]);
      setNewTagInput('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Comics Management</h1>
          <p className="text-gray-400">{total} total comics</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              type="text"
              placeholder="Search comics..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="pl-10 bg-gray-800 border-gray-700 text-white w-64"
            />
          </div>
          <Button onClick={fetchComics} variant="outline" className="border-gray-700 text-gray-300">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-4 text-gray-400 font-medium">Comic</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Author</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Episodes</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Likes</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Comments</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Categories</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Tags</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Support</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Featured</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Created</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="p-8 text-center text-gray-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : comics.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="p-8 text-center text-gray-500">No comics found</td>
                  </tr>
                ) : (
                  comics.map(comic => (
                    <tr key={comic.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-gray-800 flex items-center justify-center overflow-hidden">
                            {comic.episodes?.[0]?.slides?.[0] ? (
                              <img
                                src={comic.episodes[0].slides[0]}
                                alt={comic.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <BookOpen className="w-6 h-6 text-gray-500" />
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium truncate max-w-[200px]">{comic.title}</p>
                            <p className="text-sm text-gray-500 truncate max-w-[200px]">{comic.description || 'No description'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-gray-300">{comic.author_name}</td>
                      <td className="p-4 text-center text-gray-300">{comic.episode_count || 0}</td>
                      <td className="p-4 text-center text-gray-300">{comic.likes_count || 0}</td>
                      <td className="p-4 text-center text-gray-300">{comic.comments_count || 0}</td>
                      {/* Categories cell */}
                      <td className="p-4">
                        {editingCategoryId === comic.id ? (
                          <div className="min-w-[200px] space-y-2">
                            <div className="flex flex-wrap gap-1">
                              {PREDEFINED_CATEGORIES.map(cat => (
                                <button
                                  key={cat}
                                  type="button"
                                  onClick={() => toggleEditCategory(cat)}
                                  className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all border ${editCategories.includes(cat)
                                    ? 'bg-purple-600 text-white border-purple-500'
                                    : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-purple-500/50'
                                    }`}
                                >
                                  {cat}
                                </button>
                              ))}
                            </div>
                            <div className="flex gap-1">
                              <Input
                                value={newCatInput}
                                onChange={(e) => setNewCatInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomCategory())}
                                placeholder="Custom..."
                                className="h-6 text-[10px] bg-gray-800 border-gray-700 text-white px-2 py-0"
                              />
                              <Button type="button" onClick={addCustomCategory} size="sm" className="h-6 px-1 bg-purple-600 hover:bg-purple-700">
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                            <div className="flex gap-1 mt-1">
                              <Button onClick={() => handleSaveCategories(comic.id)} size="sm" className="h-6 px-2 text-[10px] bg-green-600 hover:bg-green-700 text-white">
                                <Save className="w-3 h-3 mr-1" /> Save
                              </Button>
                              <Button onClick={cancelEditCategories} size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-gray-400 hover:text-white">
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start gap-1">
                            <div className="flex flex-wrap gap-1 max-w-[180px]">
                              {(comic.categories || []).length > 0 ? (
                                comic.categories.map(cat => (
                                  <span key={cat} className="px-1.5 py-0.5 rounded bg-purple-600/20 text-purple-300 text-[10px] font-medium border border-purple-500/30">
                                    {cat}
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-600 text-xs italic">None</span>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => startEditCategories(comic)}
                              className="h-5 w-5 p-0 text-gray-500 hover:text-purple-400 shrink-0"
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </td>
                      {/* Tags cell */}
                      <td className="p-4">
                        {editingCategoryId === comic.id ? (
                          <div className="min-w-[200px] space-y-2">
                            <div className="flex flex-wrap gap-1">
                              {PREDEFINED_TAGS.map(tag => (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() => toggleEditTag(tag)}
                                  className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all border ${editTags.includes(tag)
                                    ? 'bg-cyan-600 text-white border-cyan-500'
                                    : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-cyan-500/50'
                                    }`}
                                >
                                  {tag}
                                </button>
                              ))}
                            </div>
                            <div className="flex gap-1">
                              <Input
                                value={newTagInput}
                                onChange={(e) => setNewTagInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomTag())}
                                placeholder="Custom..."
                                className="h-6 text-[10px] bg-gray-800 border-gray-700 text-white px-2 py-0"
                              />
                              <Button type="button" onClick={addCustomTag} size="sm" className="h-6 px-1 bg-cyan-600 hover:bg-cyan-700">
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-[180px]">
                            {(comic.tags || []).length > 0 ? (
                              comic.tags.map(tag => (
                                <span key={tag} className="px-1.5 py-0.5 rounded bg-cyan-600/20 text-cyan-300 text-[10px] font-medium border border-cyan-500/30">
                                  {tag}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-600 text-xs italic">None</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleTips(comic.id)}
                          className={comic.tips_enabled !== false ? "text-green-400 hover:text-green-300" : "text-red-400 hover:text-red-300"}
                          title={comic.tips_enabled !== false ? "Tips Enabled" : "Tips Disabled"}
                        >
                          {comic.tips_enabled !== false ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                        </Button>
                      </td>
                      <td className="p-4 text-center">
                        {comic.is_daily_featured ? (
                          <Star className="w-5 h-5 text-yellow-500 mx-auto" fill="currentColor" />
                        ) : (
                          <Star className="w-5 h-5 text-gray-600 mx-auto" />
                        )}
                      </td>
                      <td className="p-4 text-center text-gray-400 text-sm">
                        {comic.created_at ? new Date(comic.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(`/streamlined-comic/${comic.id}`, '_blank')}
                            className="text-gray-400 hover:text-white"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleFeatureComic(comic.id)}
                            className={comic.is_daily_featured ? 'text-yellow-400 hover:text-yellow-300' : 'text-gray-400 hover:text-yellow-400'}
                            title={comic.is_daily_featured ? 'Unfeature' : 'Feature'}
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteComic(comic.id, comic.title)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-gray-800">
            <p className="text-sm text-gray-400">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-gray-700 text-gray-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                className="border-gray-700 text-gray-300"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Credit Revenue Tab - NEW
const CreditRevenueTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, purchasesRes, chartRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/credits/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/credits/purchases`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { skip: page * limit, limit }
        }),
        axios.get(`${BACKEND_URL}/api/admin/credits/revenue-chart?days=14`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setPurchases(purchasesRes.data.purchases);
      setTotal(purchasesRes.data.total);
      setChartData(chartRes.data.chart_data || []);
    } catch (err) {
      toast.error('Failed to fetch credit data');
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const packageNames = {
    starter: 'Starter Pack',
    creator: 'Creator Pack',
    pro: 'Pro Pack',
    studio: 'Studio Pack'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Credit Revenue</h1>
          <p className="text-gray-400">Track credit purchases and usage</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="border-gray-700 text-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Revenue</p>
                <p className="text-3xl font-bold text-green-400">₹{(stats?.purchase_revenue || 0).toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">₹{(stats?.revenue_this_week || 0).toFixed(2)} this week</p>
              </div>
              <div className="p-3 rounded-xl bg-green-500/20">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Credits Sold</p>
                <p className="text-3xl font-bold text-purple-400">{stats?.total_credits_sold || 0}</p>
                <p className="text-xs text-gray-500 mt-1">{stats?.total_purchases || 0} purchases</p>
              </div>
              <div className="p-3 rounded-xl bg-purple-500/20">
                <Sparkles className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Credits in Circulation</p>
                <p className="text-3xl font-bold text-cyan-400">{stats?.credits_in_circulation || 0}</p>
                <p className="text-xs text-gray-500 mt-1">{stats?.credits_used || 0} used</p>
              </div>
              <div className="p-3 rounded-xl bg-cyan-500/20">
                <TrendingUp className="w-6 h-6 text-cyan-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Admin Gifted</p>
                <p className="text-3xl font-bold text-yellow-400">{stats?.admin_gifted || 0}</p>
                <p className="text-xs text-gray-500 mt-1">{stats?.admin_actions || 0} actions</p>
              </div>
              <div className="p-3 rounded-xl bg-yellow-500/20">
                <Plus className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Refunds Row */}
      {(stats?.total_refunds > 0 || stats?.refund_amount > 0) && (
        <Card className="bg-red-900/20 border-red-500/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <RefreshCw className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-red-400 font-medium">Refunds</p>
                <p className="text-sm text-gray-400">{stats?.total_refunds || 0} refunds processed</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-red-400">-₹{(stats?.refund_amount || 0).toFixed(2)}</p>
              <p className="text-sm text-gray-500">{stats?.credits_refunded || 0} credits deducted</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue Chart */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Revenue Trend</CardTitle>
          <CardDescription className="text-gray-400">Daily credit purchase revenue</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => v.slice(5)} />
                <YAxis stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => `₹${v}`} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#9CA3AF' }}
                  formatter={(value, name) => [name === 'revenue' ? `₹${value.toFixed(2)}` : value, name === 'revenue' ? 'Revenue' : name === 'credits_sold' ? 'Credits Sold' : 'Purchases']}
                />
                <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="#10B981" fillOpacity={0.3} name="revenue" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Package Breakdown */}
      {stats?.revenue_by_package?.length > 0 && (
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Revenue by Package</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.revenue_by_package.map(pkg => (
                <div key={pkg._id} className="p-4 rounded-lg bg-gray-800 border border-gray-700">
                  <p className="text-gray-400 text-sm">{packageNames[pkg._id] || pkg._id}</p>
                  <p className="text-xl font-bold text-white">{pkg.count} sold</p>
                  <p className="text-green-400 font-medium">₹{(pkg.revenue || 0).toFixed(2)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Purchase History */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Purchase History</CardTitle>
          <CardDescription className="text-gray-400">All credit purchases</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-4 text-gray-400 font-medium">Date</th>
                  <th className="text-left p-4 text-gray-400 font-medium">User</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Package</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Credits</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Amount</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-gray-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : purchases.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-gray-500">No purchases yet</td>
                  </tr>
                ) : (
                  purchases.map(purchase => (
                    <tr key={purchase.id || purchase.session_id} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="p-4 text-gray-300 text-sm">
                        {purchase.completed_at || purchase.created_at
                          ? new Date(purchase.completed_at || purchase.created_at).toLocaleString()
                          : 'N/A'}
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{purchase.user?.display_name || purchase.user?.username || purchase.username || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{purchase.user?.email || ''}</p>
                        </div>
                      </td>
                      <td className="p-4 text-gray-300">
                        {packageNames[purchase.package_id] || purchase.package_id || 'Custom'}
                      </td>
                      <td className="p-4 text-center">
                        <span className="text-purple-400 font-bold">{purchase.credits}</span>
                      </td>
                      <td className="p-4 text-center text-green-400 font-medium">
                        ₹{(purchase.amount || 0).toFixed(2)}
                      </td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${purchase.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          purchase.status === 'refunded' ? 'bg-red-500/20 text-red-400' :
                            purchase.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-gray-500/20 text-gray-400'
                          }`}>
                          {purchase.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-gray-800">
            <p className="text-sm text-gray-400">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-gray-700 text-gray-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                className="border-gray-700 text-gray-300"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Webhook Info */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Activity className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-white font-medium">Automatic Credit System Active</p>
              <p className="text-sm text-gray-400 mt-1">
                Credits are automatically added when customers complete payment via Stripe webhook.
                Refunds are also automatically processed.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Webhook endpoint: <code className="bg-gray-900 px-2 py-0.5 rounded">/api/stripe/webhook</code>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Tips Revenue Tab (renamed from Revenue)
const RevenueTab = ({ token, stats }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { skip: page * limit, limit }
      });
      setTransactions(response.data.transactions);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Revenue & Payments</h1>
        <p className="text-gray-400">Platform earnings and transaction history</p>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <p className="text-sm text-gray-400">Total Revenue</p>
            <p className="text-3xl font-bold text-white">₹{(stats?.revenue?.total_revenue || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">All time</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <p className="text-sm text-gray-400">Platform Fees (5%)</p>
            <p className="text-3xl font-bold text-green-400">₹{(stats?.revenue?.platform_fees || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">Your earnings</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <p className="text-sm text-gray-400">Creator Payouts</p>
            <p className="text-3xl font-bold text-purple-400">₹{(stats?.revenue?.creator_payouts || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">Sent to creators</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <p className="text-sm text-gray-400">Total Transactions</p>
            <p className="text-3xl font-bold text-cyan-400">{stats?.revenue?.total_transactions || 0}</p>
            <p className="text-xs text-gray-500 mt-1">{stats?.revenue?.transactions_this_week || 0} this week</p>
          </CardContent>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Transaction History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-4 text-gray-400 font-medium">Date</th>
                  <th className="text-left p-4 text-gray-400 font-medium">From</th>
                  <th className="text-left p-4 text-gray-400 font-medium">To</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Amount</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Platform Fee</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-gray-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-gray-500">No transactions yet</td>
                  </tr>
                ) : (
                  transactions.map(tx => (
                    <tr key={tx.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="p-4 text-gray-300 text-sm">
                        {tx.created_at ? new Date(tx.created_at).toLocaleString() : 'N/A'}
                      </td>
                      <td className="p-4 text-gray-300">
                        {tx.tipper?.display_name || tx.tipper?.username || 'Unknown'}
                      </td>
                      <td className="p-4 text-gray-300">
                        {tx.creator?.display_name || tx.creator?.username || 'Unknown'}
                      </td>
                      <td className="p-4 text-center text-white font-medium">
                        ₹{(tx.amount || 0).toFixed(2)}
                      </td>
                      <td className="p-4 text-center text-green-400">
                        ₹{(tx.platform_fee || 0).toFixed(2)}
                      </td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${tx.payment_status === 'paid'
                          ? 'bg-green-500/20 text-green-400'
                          : tx.payment_status === 'pending'
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-red-500/20 text-red-400'
                          }`}>
                          {tx.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-gray-800">
            <p className="text-sm text-gray-400">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-gray-700 text-gray-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                className="border-gray-700 text-gray-300"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Posts Tab
const PostsTab = ({ token }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit, search: search || undefined };
      const response = await axios.get(`${BACKEND_URL}/api/admin/community/posts`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setPosts(response.data.posts);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Fetch posts error:', err);
      toast.error(err.response?.data?.detail || err.message || 'Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  }, [token, page, search]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/community/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete post');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Community Posts</h1>
          <p className="text-gray-400">{total} total posts</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              type="text"
              placeholder="Search posts..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="pl-10 bg-gray-800 border-gray-700 text-white w-64"
            />
          </div>
          <Button onClick={fetchPosts} variant="outline" className="border-gray-700 text-gray-300">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-4 text-gray-400 font-medium">Post Content</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Author</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Images</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Engagement</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Created</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-gray-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : posts.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-gray-500">No posts found</td>
                  </tr>
                ) : (
                  posts.map(post => (
                    <tr key={post.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="p-4 max-w-xs">
                        <p className="text-white truncate">{post.content}</p>
                        <div className="flex gap-1 mt-1">
                          <span className="text-xs bg-purple-900/40 text-purple-400 px-1.5 py-0.5 rounded uppercase">{post.category}</span>
                        </div>
                      </td>
                      <td className="p-4 text-gray-300">
                        <div className="flex flex-col">
                          <span>{post.author_name}</span>
                          <span className="text-xs text-gray-500">{post.author_handle}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        {post.images && post.images.length > 0 ? (
                          <div className="flex justify-center -space-x-2">
                            {post.images.slice(0, 3).map((img, i) => (
                              <img key={i} src={img} alt="" className="w-8 h-8 rounded-full border border-gray-800 object-cover" />
                            ))}
                            {post.images.length > 3 && (
                              <div className="w-8 h-8 rounded-full bg-gray-800 border-gray-700 flex items-center justify-center text-xs text-gray-400">+{post.images.length - 3}</div>
                            )}
                          </div>
                        ) : post.image ? (
                          <div className="flex justify-center">
                            <img src={post.image} alt="" className="w-8 h-8 rounded border border-gray-800 object-cover" />
                          </div>
                        ) : (
                          <span className="text-gray-600">-</span>
                        )}
                      </td>
                      <td className="p-4 text-center text-gray-300">
                        <span className="text-pink-400">{post.likes_count}</span>{' / '}
                        <span className="text-blue-400">{post.comments_count}</span>
                      </td>
                      <td className="p-4 text-center text-gray-400 text-sm">
                        {new Date(post.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletePost(post.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-gray-800">
            <p className="text-sm text-gray-400">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-gray-700 text-gray-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                className="border-gray-700 text-gray-300"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Messages Tab - NEW
const MessagesTab = ({ token }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [openMsgId, setOpenMsgId] = useState(null);
  const limit = 20;

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/contact-messages`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { skip: page * limit, limit }
      });
      setMessages(response.data.messages);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const markAsRead = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${BACKEND_URL}/api/admin/contact-messages/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(messages.map(m => m.id === id ? { ...m, status: 'read' } : m));
      toast.success('Marked as read');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Contact Messages</h1>
          <p className="text-gray-400">{total} total messages</p>
        </div>
        <Button onClick={fetchMessages} variant="outline" className="border-gray-700 text-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0">
          <div className="divide-y divide-gray-800">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
              </div>
            ) : messages.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No messages found</div>
            ) : (
              messages.map(msg => (
                <div
                  key={msg.id}
                  className={`group hover:bg-gray-800/50 transition-colors ${msg.status === 'unread' ? 'bg-gray-800/20' : ''}`}
                >
                  <div
                    onClick={() => setOpenMsgId(openMsgId === msg.id ? null : msg.id)}
                    className="p-4 flex items-center justify-between cursor-pointer"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${msg.status === 'unread' ? 'bg-green-500' : 'bg-transparent'}`} />

                      <div className="min-w-[150px]">
                        <p className={`text-white truncate ${msg.status === 'unread' ? 'font-semibold' : ''}`}>{msg.name}</p>
                        <p className="text-xs text-gray-500 truncate">{msg.email}</p>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={`text-gray-300 truncate ${msg.status === 'unread' ? 'font-medium text-gray-200' : ''}`}>
                          {msg.subject}
                        </p>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {/* Preview first 60 chars */}
                          {msg.message.slice(0, 80)}...
                        </p>
                      </div>

                      <div className="text-right text-xs text-gray-500 min-w-[100px]">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="ml-4">
                      <ChevronRight className={`w-5 h-5 text-gray-500 transition-transform ${openMsgId === msg.id ? 'rotate-90' : ''}`} />
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {openMsgId === msg.id && (
                    <div className="px-4 pb-4 pl-10">
                      <div className="p-4 rounded-lg bg-gray-800 border border-gray-700 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-lg font-semibold text-white">{msg.subject}</h4>
                          {msg.status === 'unread' && (
                            <Button size="sm" onClick={(e) => markAsRead(msg.id, e)} variant="outline" className="border-gray-600">
                              Mark as Read
                            </Button>
                          )}
                        </div>
                        <div className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                          {msg.message}
                        </div>
                        <div className="pt-4 mt-4 border-t border-gray-700 flex items-center gap-2">
                          <a href={`mailto:${msg.email}`} className="text-sm text-purple-400 hover:text-purple-300 hover:underline">
                            Reply to {msg.email}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-gray-800">
            <p className="text-sm text-gray-400">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-gray-700 text-gray-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                className="border-gray-700 text-gray-300"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Activity Tab
const ActivityTab = ({ activity, loading }) => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Activity Feed</h1>
        <p className="text-gray-400">Recent platform activity and events</p>
      </div>

      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-6">
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
              </div>
            ) : activity?.length > 0 ? (
              activity.map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 rounded-lg bg-gray-800 border border-gray-700">
                  <div className={`p-3 rounded-xl ${item.type === 'user_registration' ? 'bg-purple-500/20 text-purple-400' :
                    item.type === 'comic_created' ? 'bg-cyan-500/20 text-cyan-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                    {item.type === 'user_registration' ? <Users className="w-5 h-5" /> :
                      item.type === 'comic_created' ? <BookOpen className="w-5 h-5" /> :
                        <DollarSign className="w-5 h-5" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-white">{item.message}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {item.timestamp ? new Date(item.timestamp).toLocaleString() : 'Just now'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No recent activity</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// CARAVAGGIO Dataset Manager Tab
const CavaggioTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [images, setImages] = useState([]);
  const [total, setTotal] = useState(0);
  const [captionedCount, setCaptionedCount] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editCaption, setEditCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showTrainModal, setShowTrainModal] = useState(false);
  const [trainConfig, setTrainConfig] = useState({ max_train_steps: '', learning_rate: '' });
  const [triggering, setTriggering] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const limit = 30;

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cavaggio/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Stats error:', err);
    }
  }, [token]);

  // Fetch dataset images
  const fetchImages = useCallback(async () => {
    setImagesLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/dataset`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { skip: page * limit, limit, search, type_filter: typeFilter }
      });
      setImages(response.data.images);
      setTotal(response.data.total);
      setCaptionedCount(response.data.captioned || 0);
    } catch (err) {
      toast.error('Failed to fetch dataset');
    } finally {
      setImagesLoading(false);
    }
  }, [token, page, search, typeFilter]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchStats();
      await fetchImages();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => { fetchImages(); }, [page, search, typeFilter]);

  // Save caption
  const saveCaption = async (imageId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/dataset/${imageId}/caption`,
        { caption: editCaption },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImages(imgs => imgs.map(i => i.id === imageId ? { ...i, gemini_caption: editCaption } : i));
      setEditingId(null);
      toast.success('Caption saved');
    } catch (err) {
      toast.error('Failed to save caption');
    }
  };

  // Delete image
  const deleteImage = async (imageId) => {
    if (!window.confirm('Delete this image from the training dataset?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/dataset/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(imgs => imgs.filter(i => i.id !== imageId));
      setTotal(t => t - 1);
      toast.success('Image deleted');
    } catch (err) {
      toast.error('Failed to delete image');
    }
  };

  // Upload files
  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(f => formData.append('files', f));

      await axios.post(`${BACKEND_URL}/api/admin/dataset/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`${files.length} image(s) uploaded! Gemini captioning in progress...`);
      fetchImages();
      fetchStats();
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  // Trigger training
  const triggerTraining = async () => {
    setTriggering(true);
    try {
      const body = {};
      if (trainConfig.max_train_steps) body.max_train_steps = parseInt(trainConfig.max_train_steps);
      if (trainConfig.learning_rate) body.learning_rate = parseFloat(trainConfig.learning_rate);

      const res = await axios.post(`${BACKEND_URL}/api/admin/dataset/trigger-training`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setShowTrainModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Training trigger failed');
    } finally {
      setTriggering(false);
    }
  };

  // Drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  const typeLabels = {
    'sketch-to-image': { label: 'Sketch → AI', color: 'bg-purple-500' },
    'text-to-image': { label: 'Text → Image', color: 'bg-cyan-500' },
    'batch-panel': { label: 'Batch Panels', color: 'bg-blue-500' },
    'match-style': { label: 'Match Style', color: 'bg-pink-500' },
    'inpainting': { label: 'Inpainting', color: 'bg-orange-500' },
    'admin-upload': { label: 'Admin Upload', color: 'bg-green-500' },
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center">
              <Palette className="w-6 h-6 text-white" />
            </div>
            CARAVAGGIO Dataset Manager
          </h1>
          <p className="text-gray-400 mt-1">Manage training data, captions, and trigger LoRA training</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => { fetchStats(); fetchImages(); }} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowTrainModal(true)} className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold">
            🚀 Trigger Training
          </Button>
        </div>
      </div>

      {/* Progress Bar toward 100K */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-400" />
              <span className="text-white font-semibold">Training Data Goal</span>
            </div>
            <span className="text-amber-400 font-bold text-lg">
              {(stats?.total_images || 0).toLocaleString()} / {(stats?.goal || 100000).toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-4 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500"
              style={{ width: `${Math.min(stats?.progress_pct || 0, 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-gray-500">{stats?.progress_pct || 0}% complete</span>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                {captionedCount} / {stats?.total_images || 0} captioned
              </span>
              <span className="text-sm text-gray-500">
                {stats?.days_to_goal ? `~${stats.days_to_goal} days remaining` : ''}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title="Total Images" value={(stats?.total_images || 0).toLocaleString()} icon={Image} color="bg-amber-600" subtitle={`${stats?.days_collecting || 0} days collecting`} />
        <StatsCard title="Today" value={stats?.today || 0} icon={Target} color="bg-green-600" subtitle="images collected today" />
        <StatsCard title="Daily Average" value={stats?.daily_avg || 0} icon={TrendingUp} color="bg-cyan-600" subtitle="images per day" />
        <StatsCard title="Storage Used" value={`${stats?.total_storage_mb || 0} MB`} icon={Database} color="bg-purple-600" subtitle={`${stats?.this_week || 0} images this week`} />
      </div>

      {/* Upload Zone */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-6">
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${dragOver ? 'border-amber-400 bg-amber-500/10' : 'border-gray-700 hover:border-gray-600 hover:bg-gray-800/50'
              }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('dataset-file-input').click()}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
                <p className="text-gray-300">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Plus className="w-8 h-8 text-gray-500" />
                <p className="text-gray-300 font-medium">Drop images here or click to upload</p>
                <p className="text-sm text-gray-500">Supports PNG, JPG, WEBP • Multiple files • Gemini captions auto-generated</p>
              </div>
            )}
            <input
              id="dataset-file-input"
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Image Gallery */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Dataset Images</CardTitle>
              <CardDescription className="text-gray-400">{total.toLocaleString()} images total • Page {page + 1} of {totalPages || 1}</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder="Search captions..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  className="pl-9 bg-gray-800 border-gray-700 text-white w-56"
                />
              </div>
              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
                className="bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Types</option>
                {Object.entries(typeLabels).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {imagesLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          ) : images.length === 0 ? (
            <p className="text-gray-500 text-center py-12">No images found{search ? ` matching "${search}"` : ''}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map(img => (
                <div key={img.id} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden group hover:border-gray-600 transition-all">
                  {/* Image */}
                  <div className="aspect-square relative overflow-hidden bg-gray-900">
                    <img
                      src={img.image_url}
                      alt={img.gemini_caption || img.prompt || 'Training image'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                      loading="lazy"
                      onClick={() => setSelectedImage(img)}
                    />
                    {/* Type Badge */}
                    <div className="absolute top-2 left-2">
                      <span className={`text-xs px-2 py-1 rounded-full text-white/90 ${typeLabels[img.type]?.color || 'bg-gray-600'}`}>
                        {typeLabels[img.type]?.label || img.type}
                      </span>
                    </div>
                    {/* Caption status indicator */}
                    <div className="absolute top-2 right-2">
                      {img.gemini_caption ? (
                        <span className="w-3 h-3 rounded-full bg-green-500 block" title="Captioned" />
                      ) : (
                        <span className="w-3 h-3 rounded-full bg-yellow-500 block animate-pulse" title="Pending caption" />
                      )}
                    </div>
                    {/* Delete button */}
                    <button
                      onClick={() => deleteImage(img.id)}
                      className="absolute bottom-2 right-2 p-1.5 rounded-lg bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      title="Delete image"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Caption Area */}
                  <div className="p-3 space-y-2">
                    {/* User Prompt */}
                    {img.prompt && (
                      <div>
                        <span className="text-xs text-gray-500 uppercase font-medium">User Prompt</span>
                        <p className="text-xs text-gray-400 line-clamp-2 mt-0.5">{img.prompt}</p>
                      </div>
                    )}

                    {/* Gemini Caption — Editable */}
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 uppercase font-medium">AI Caption</span>
                        {editingId !== img.id && (
                          <button
                            onClick={() => { setEditingId(img.id); setEditCaption(img.gemini_caption || ''); }}
                            className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                          >
                            <Edit2 className="w-3 h-3" /> Edit
                          </button>
                        )}
                      </div>

                      {editingId === img.id ? (
                        <div className="mt-1 space-y-2">
                          <textarea
                            value={editCaption}
                            onChange={(e) => setEditCaption(e.target.value)}
                            className="w-full bg-gray-900 border border-gray-600 rounded-lg p-2 text-sm text-white resize-none focus:border-purple-500 focus:outline-none"
                            rows={3}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => saveCaption(img.id)} className="bg-purple-600 hover:bg-purple-700 text-white text-xs py-1 px-3 h-7">
                              <Save className="w-3 h-3 mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="text-gray-400 text-xs py-1 px-3 h-7">
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-300 mt-0.5 line-clamp-3">
                          {img.gemini_caption || <span className="text-yellow-500 italic">Generating caption...</span>}
                        </p>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="flex items-center justify-between pt-1 border-t border-gray-700">
                      <span className="text-xs text-gray-600">
                        {img.width}×{img.height}
                      </span>
                      <span className="text-xs text-gray-600">
                        {img.created_at ? new Date(img.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-6 mt-4 border-t border-gray-800">
              <p className="text-sm text-gray-400">
                Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total.toLocaleString()}
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="border-gray-700 text-gray-300">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-400 px-2">
                  {page + 1} / {totalPages}
                </span>
                <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages} className="border-gray-700 text-gray-300">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Training Trigger Modal */}
      {showTrainModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowTrainModal(false)}>
          <Card className="bg-gray-900 border-gray-800 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-xl">
                  🚀
                </div>
                Trigger LoRA Training
              </CardTitle>
              <CardDescription className="text-gray-400">
                This will start a training job on RunPod with {(stats?.total_images || 0).toLocaleString()} images.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-sm text-amber-300">
                  ⚠️ Training typically takes 1-4 hours depending on step count.
                  {captionedCount < (stats?.total_images || 0) && (
                    <span className="block mt-1 text-yellow-400">
                      Note: {(stats?.total_images || 0) - captionedCount} images are still pending Gemini captions.
                    </span>
                  )}
                </p>
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-1 block">Training Steps (optional)</label>
                <Input
                  type="number"
                  placeholder="Auto-calculated"
                  value={trainConfig.max_train_steps}
                  onChange={e => setTrainConfig({ ...trainConfig, max_train_steps: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
                <p className="text-xs text-gray-600 mt-1">Leave empty to auto-calculate based on dataset size</p>
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-1 block">Learning Rate (optional)</label>
                <Input
                  type="number"
                  step="0.00001"
                  placeholder="Default: from config"
                  value={trainConfig.learning_rate}
                  onChange={e => setTrainConfig({ ...trainConfig, learning_rate: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={() => setShowTrainModal(false)} variant="outline" className="flex-1 border-gray-700 text-gray-300">Cancel</Button>
                <Button
                  onClick={triggerTraining}
                  disabled={triggering}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold"
                >
                  {triggering ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : '🚀 '}
                  {triggering ? 'Triggering...' : 'Start Training'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Image Zoom Lightbox */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedImage(null)}>
          <div className="relative max-w-4xl max-h-[90vh] w-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
            {/* Close button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-2 -right-2 z-10 p-2 rounded-full bg-gray-800 text-white hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Zoomed Image */}
            <img
              src={selectedImage.image_url}
              alt={selectedImage.gemini_caption || selectedImage.prompt || 'Training image'}
              className="max-h-[70vh] w-auto rounded-xl object-contain"
            />

            {/* Caption & Info Below Image */}
            <div className="mt-4 w-full max-w-2xl bg-gray-900 rounded-xl p-4 border border-gray-700">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs px-2 py-1 rounded-full text-white/90 ${typeLabels[selectedImage.type]?.color || 'bg-gray-600'}`}>
                  {typeLabels[selectedImage.type]?.label || selectedImage.type}
                </span>
                <span className="text-xs text-gray-500">
                  {selectedImage.width}×{selectedImage.height}
                </span>
                <span className="text-xs text-gray-500">
                  {selectedImage.created_at ? new Date(selectedImage.created_at).toLocaleDateString() : ''}
                </span>
              </div>
              {selectedImage.gemini_caption && (
                <div className="mb-2">
                  <span className="text-xs text-amber-400 uppercase font-medium">AI Caption</span>
                  <p className="text-sm text-gray-200 mt-1">{selectedImage.gemini_caption}</p>
                </div>
              )}
              {selectedImage.prompt && (
                <div>
                  <span className="text-xs text-gray-500 uppercase font-medium">User Prompt</span>
                  <p className="text-sm text-gray-400 mt-1">{selectedImage.prompt}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Store Management Tab
const StoreTab = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [statusCounts, setStatusCounts] = useState({});
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [form, setForm] = useState({
    name: '', description: '', category: 'mugs', price: 499,
    currency: 'INR', image_url: '', images: [], variants: [],
    visible: true, featured: false, stock_status: 'in_stock'
  });

  const categories = [
    { value: 'all', label: 'All' },
    { value: 'mugs', label: 'Mugs' },
    { value: 'tshirts', label: 'T-Shirts' },
    { value: 'mousepads', label: 'Mousepads' },
    { value: 'posters', label: 'Posters' },
    { value: 'collectibles', label: 'Collectibles' },
  ];

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterCategory !== 'all' ? { category: filterCategory } : {};
      const res = await axios.get(`${BACKEND_URL}/api/admin/store/products`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setProducts(res.data.products || []);
    } catch (err) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [token, filterCategory]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const params = orderStatusFilter !== 'all' ? { status: orderStatusFilter } : {};
      const res = await axios.get(`${BACKEND_URL}/api/admin/store/orders`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setOrders(res.data.orders || []);
      setOrdersTotal(res.data.total || 0);
      setStatusCounts(res.data.status_counts || {});
    } catch (err) {
      toast.error('Failed to fetch orders');
    } finally {
      setOrdersLoading(false);
    }
  }, [token, orderStatusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Auto-refresh orders every 30s
  useEffect(() => {
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const openAddModal = () => {
    setEditProduct(null);
    setForm({
      name: '', description: '', category: 'mugs', price: 499,
      currency: 'INR', image_url: '', images: [], variants: [],
      visible: true, featured: false, stock_status: 'in_stock'
    });
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditProduct(product);
    setForm({
      name: product.name || '',
      description: product.description || '',
      category: product.category || 'mugs',
      price: product.price || 0,
      currency: product.currency || 'INR',
      image_url: product.image_url || '',
      images: product.images || [],
      variants: product.variants || [],
      visible: product.visible !== false,
      featured: product.featured || false,
      stock_status: product.stock_status || 'in_stock'
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || form.price < 1) { toast.error('Name and price required'); return; }
    try {
      if (editProduct) {
        await axios.put(`${BACKEND_URL}/api/admin/store/products/${editProduct.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/store/products`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created');
      }
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/store/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
      fetchProducts();
    } catch (err) {
      toast.error('Failed to delete product');
    }
  };

  const handleToggleVisibility = async (id) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/store/products/${id}/toggle-visibility`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.visible ? 'Product now visible' : 'Product hidden');
      fetchProducts();
    } catch (err) {
      toast.error('Failed to toggle visibility');
    }
  };

  const catEmoji = { mugs: '☕', tshirts: '👕', mousepads: '🖱️', posters: '🖼️', collectibles: '✨' };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Store Management</h1>
          <p className="text-gray-400">{products.length} products</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <Button onClick={fetchProducts} variant="outline" className="border-gray-700 text-gray-300">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={openAddModal} className="bg-purple-600 hover:bg-purple-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add Product
          </Button>
        </div>
      </div>

      {/* Products Table */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-4 text-gray-400 font-medium">Product</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Category</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Price</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Status</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Visible</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="p-8 text-center text-gray-500"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></td></tr>
                ) : products.length === 0 ? (
                  <tr><td colSpan="6" className="p-8 text-center text-gray-500">No products found. Click "Add Product" to create one.</td></tr>
                ) : (
                  products.map(p => (
                    <tr key={p.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-gray-800 flex items-center justify-center text-xl">
                            {catEmoji[p.category] || '📦'}
                          </div>
                          <div>
                            <p className="text-white font-medium">{p.name}</p>
                            <p className="text-xs text-gray-500 truncate max-w-[200px]">{p.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-gray-300 capitalize">{p.category}</td>
                      <td className="p-4 text-center text-white font-bold">₹{p.price}</td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${p.stock_status === 'in_stock' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                          {p.stock_status === 'in_stock' ? 'In Stock' : 'Out of Stock'}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        {p.visible ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">Visible</span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-gray-500/20 text-gray-400">Hidden</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => openEditModal(p)} className="text-blue-400 hover:text-blue-300" title="Edit">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleToggleVisibility(p.id)} className="text-gray-400 hover:text-white" title={p.visible ? 'Hide' : 'Show'}>
                            {p.visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id, p.name)} className="text-red-400 hover:text-red-300" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <Card className="bg-gray-900 border-gray-800 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Package className="w-5 h-5 text-purple-400" />
                </div>
                {editProduct ? 'Edit Product' : 'Add New Product'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Name */}
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Product Name *</label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="e.g. Comic Hero Mug" />
              </div>

              {/* Description */}
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Description</label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Brief product description" />
              </div>

              {/* Category + Price */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-400 text-sm mb-1 block">Category *</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm">
                    {categories.filter(c => c.value !== 'all').map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-sm mb-1 block">Price (₹) *</label>
                  <Input type="number" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="bg-gray-800 border-gray-700 text-white" min="1" />
                </div>
              </div>

              {/* Image URL */}
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Image URL</label>
                <Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value, images: [e.target.value] })} className="bg-gray-800 border-gray-700 text-white" placeholder="https://... or /store/image.png" />
              </div>

              {/* Toggles */}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.visible} onChange={e => setForm({ ...form, visible: e.target.checked })} className="rounded" />
                  <span className="text-gray-300 text-sm">Visible</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} className="rounded" />
                  <span className="text-gray-300 text-sm">Featured</span>
                </label>
              </div>

              {/* Stock Status */}
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Stock Status</label>
                <select value={form.stock_status} onChange={e => setForm({ ...form, stock_status: e.target.value })} className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm">
                  <option value="in_stock">In Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div>

              {/* Size Variants (for tshirts) */}
              {form.category === 'tshirts' && (
                <div className="p-3 rounded-lg bg-gray-800 border border-gray-700">
                  <p className="text-gray-400 text-sm mb-2">Size Variants (auto-added for T-Shirts)</p>
                  <div className="flex flex-wrap gap-2">
                    {['S', 'M', 'L', 'XL', 'XXL'].map(s => (
                      <span key={s} className="px-3 py-1 rounded-full text-xs bg-purple-500/20 text-purple-300">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button onClick={() => setShowModal(false)} variant="outline" className="flex-1 border-gray-700 text-gray-300">Cancel</Button>
                <Button onClick={() => {
                  // Auto-add size variants for tshirts
                  if (form.category === 'tshirts' && form.variants.length === 0) {
                    form.variants = [{ type: 'size', options: ['S', 'M', 'L', 'XL', 'XXL'] }];
                  }
                  handleSave();
                }} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold">
                  {editProduct ? 'Update Product' : 'Create Product'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ══════════ ORDERS MANAGEMENT ══════════ */}
      <div className="mt-10 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">All Orders</h2>
            <p className="text-gray-400">{ordersTotal} total orders · Auto-refreshes every 30s</p>
          </div>
          <Button onClick={fetchOrders} variant="outline" className="border-gray-700 text-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Status filter pills */}
        <div className="flex flex-wrap gap-2">
          {['all', 'payment_pending', 'confirmed', 'in_production', 'shipped', 'delivered', 'canceled'].map(st => {
            const count = st === 'all'
              ? Object.values(statusCounts).reduce((a, b) => a + b, 0)
              : (statusCounts[st] || 0);
            const colors = {
              all: 'bg-purple-600',
              payment_pending: 'bg-yellow-600',
              confirmed: 'bg-blue-600',
              in_production: 'bg-indigo-600',
              shipped: 'bg-cyan-600',
              delivered: 'bg-green-600',
              canceled: 'bg-red-600',
            };
            return (
              <button
                key={st}
                onClick={() => setOrderStatusFilter(st)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${orderStatusFilter === st
                  ? `${colors[st]} text-white`
                  : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
              >
                {st === 'all' ? 'All' : st.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${orderStatusFilter === st ? 'bg-white/20' : 'bg-gray-700'
                  }`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Orders Table */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left p-4 text-gray-400 font-medium text-sm">Order ID</th>
                    <th className="text-left p-4 text-gray-400 font-medium text-sm">Buyer</th>
                    <th className="text-left p-4 text-gray-400 font-medium text-sm">Product</th>
                    <th className="text-center p-4 text-gray-400 font-medium text-sm">Amount</th>
                    <th className="text-center p-4 text-gray-400 font-medium text-sm">Status</th>
                    <th className="text-center p-4 text-gray-400 font-medium text-sm">Printful</th>
                    <th className="text-center p-4 text-gray-400 font-medium text-sm">Tracking</th>
                    <th className="text-right p-4 text-gray-400 font-medium text-sm">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {ordersLoading ? (
                    <tr><td colSpan="8" className="p-8 text-center text-gray-500"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></td></tr>
                  ) : orders.length === 0 ? (
                    <tr><td colSpan="8" className="p-8 text-center text-gray-500">No orders found</td></tr>
                  ) : (
                    orders.map(order => {
                      const statusColors = {
                        payment_pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                        confirmed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                        in_production: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
                        shipped: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
                        delivered: 'bg-green-500/20 text-green-400 border-green-500/30',
                        canceled: 'bg-red-500/20 text-red-400 border-red-500/30',
                      };
                      const sc = statusColors[order.status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
                      return (
                        <tr key={order.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                          <td className="p-4">
                            <span className="text-gray-300 text-xs font-mono">{(order.id || '').slice(0, 12)}...</span>
                          </td>
                          <td className="p-4">
                            <p className="text-white text-sm font-medium">{order.buyer_name || order.buyer_id?.slice(0, 8) || '—'}</p>
                            <p className="text-gray-500 text-xs">{order.shipping_address?.email || ''}</p>
                          </td>
                          <td className="p-4">
                            <p className="text-white text-sm">{order.product_name || '—'}</p>
                            <p className="text-gray-500 text-xs">{order.variant_label || ''}</p>
                          </td>
                          <td className="p-4 text-center">
                            <span className="text-white font-bold">₹{(order.total_amount || order.amount || 0).toLocaleString('en-IN')}</span>
                          </td>
                          <td className="p-4 text-center">
                            <span className={`text-xs px-2.5 py-1 rounded-full border ${sc}`}>
                              {(order.status || 'unknown').replace(/_/g, ' ')}
                            </span>
                            {order.status === 'canceled' && order.canceled_at && (
                              <p className="text-red-400/60 text-xs mt-1">
                                {new Date(order.canceled_at).toLocaleDateString()}
                              </p>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {order.printful_order_id ? (
                              <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">Synced</span>
                            ) : order.printful_canceled ? (
                              <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">Canceled</span>
                            ) : (
                              <span className="text-xs px-2 py-1 rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">—</span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {order.tracking_url ? (
                              <a href={order.tracking_url} target="_blank" rel="noreferrer"
                                className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center justify-center gap-1">
                                <ExternalLink className="w-3 h-3" />
                                {order.tracking_number || 'Track'}
                              </a>
                            ) : (
                              <span className="text-gray-600 text-xs">—</span>
                            )}
                          </td>
                          <td className="p-4 text-right text-gray-400 text-xs">
                            {order.created_at ? new Date(order.created_at).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Feature Toggles Tab
const FeatureTogglesTab = ({ token }) => {
  const [settings, setSettings] = useState({ merch_enabled: false, earnings_enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/settings/features`);
        setSettings(res.data);
      } catch (e) {
        console.error('Settings fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const toggleFeature = async (feature) => {
    setSaving(true);
    try {
      const newVal = !settings[feature];
      await axios.put(`${BACKEND_URL}/api/admin/settings/features`, {
        [feature]: newVal
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, [feature]: newVal }));
      toast.success(`${feature.replace('_', ' ')} ${newVal ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const ToggleSwitch = ({ enabled, onToggle, disabled }) => (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`relative w-14 h-7 rounded-full transition-all duration-300 ${enabled ? 'bg-green-500' : 'bg-gray-600'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-300 ${enabled ? 'translate-x-7' : 'translate-x-0.5'
        }`} />
    </button>
  );

  if (loading) {
    return <div className="flex justify-center py-20"><Sparkles className="w-8 h-8 text-purple-400 animate-spin" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white">Feature Toggles</h2>
          <p className="text-gray-400 text-sm mt-1">Enable or disable platform features</p>
        </div>
        <Settings className="w-8 h-8 text-purple-400" />
      </div>

      <div className="space-y-4">
        <div className="bg-gray-800/60 rounded-xl p-6 border border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Creator Merch Pipeline</h3>
              <p className="text-gray-400 text-sm">Allow creators to submit merch products for review</p>
            </div>
          </div>
          <ToggleSwitch
            enabled={settings.merch_enabled}
            onToggle={() => toggleFeature('merch_enabled')}
            disabled={saving}
          />
        </div>

        <div className="bg-gray-800/60 rounded-xl p-6 border border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <IndianRupee className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Earnings Dashboard</h3>
              <p className="text-gray-400 text-sm">Allow creators to view their merch earnings</p>
            </div>
          </div>
          <ToggleSwitch
            enabled={settings.earnings_enabled}
            onToggle={() => toggleFeature('earnings_enabled')}
            disabled={saving}
          />
        </div>
      </div>
    </div>
  );
};

// User Submissions Tab
const UserSubmissionsTab = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${BACKEND_URL}/api/admin/store/user-products${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(res.data.products || []);
    } catch (e) {
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleApprove = async (productId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/store/user-products/${productId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product approved!');
      fetchProducts();
    } catch (e) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/store/user-products/${rejectModal}/reject`, {
        reason: rejectReason || 'Does not meet quality standards'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product rejected');
      setRejectModal(null);
      setRejectReason('');
      fetchProducts();
    } catch (e) {
      toast.error('Failed to reject');
    }
  };

  const statusColors = {
    pending_review: 'text-yellow-400 bg-yellow-500/20',
    approved: 'text-green-400 bg-green-500/20',
    rejected: 'text-red-400 bg-red-500/20',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">User Submissions</h2>
          <p className="text-gray-400 text-sm mt-1">Review and manage creator-submitted products</p>
        </div>
        <button onClick={fetchProducts} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-6">
        {['all', 'pending_review', 'approved', 'rejected'].map(status => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${statusFilter === status
              ? 'bg-purple-600 text-white'
              : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
          >
            {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Sparkles className="w-8 h-8 text-purple-400 animate-spin" /></div>
      ) : products.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No submissions found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {products.map(product => (
            <div key={product.id} className="bg-gray-800/60 rounded-xl p-5 border border-gray-700 flex items-center gap-4">
              {/* Product Image */}
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-700">
                {product.image_url || product.mockup_url ? (
                  <img src={product.mockup_url || product.image_url} alt={product.name}
                    className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-6 h-6 text-gray-500" />
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-semibold text-sm truncate">{product.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[product.status] || 'text-gray-400 bg-gray-700'}`}>
                    {product.status?.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-gray-500 text-xs">
                  By <span className="text-gray-400">{product.creator_name}</span> · {product.category} · ₹{product.price}
                  {product.creator_markup > 0 && <span className="text-green-400"> (markup: ₹{product.creator_markup})</span>}
                </p>
                {product.rejection_reason && (
                  <p className="text-red-400/70 text-xs mt-1 italic">Rejected: "{product.rejection_reason}"</p>
                )}
              </div>

              {/* Actions */}
              {product.status === 'pending_review' && (
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleApprove(product.id)}
                    className="bg-green-600/20 hover:bg-green-600/40 text-green-400 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 border border-green-500/30"
                  >
                    <CheckCircle className="w-4 h-4" /> Approve
                  </button>
                  <button
                    onClick={() => setRejectModal(product.id)}
                    className="bg-red-600/20 hover:bg-red-600/40 text-red-400 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 border border-red-500/30"
                  >
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setRejectModal(null)}>
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4 border border-gray-700" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg mb-3">Reject Product</h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-4 py-3 h-24 resize-none focus:ring-2 focus:ring-red-500 mb-4"
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setRejectModal(null)} className="px-6 py-2 text-gray-400 hover:text-white">Cancel</button>
              <button onClick={handleReject} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Creator Earnings Overview Tab (Admin)
const CreatorEarningsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchEarnings = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/earnings/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load earnings overview');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchEarnings(); }, [fetchEarnings]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(fetchEarnings, 30000);
    return () => clearInterval(interval);
  }, [fetchEarnings]);

  if (loading) {
    return <div className="flex justify-center py-20"><Sparkles className="w-8 h-8 text-purple-400 animate-spin" /></div>;
  }

  const overview = data || { creators: [], total_platform_revenue: 0, total_creator_payouts: 0, total_creators: 0 };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">Creator Earnings Overview</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">Auto-refreshes every 30s</span>
          <Button onClick={fetchEarnings} variant="outline" className="border-gray-700 text-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gray-800/60 rounded-xl p-5 border border-gray-700">
          <p className="text-gray-400 text-sm mb-1">Platform Revenue</p>
          <p className="text-3xl font-bold text-green-400">₹{overview.total_platform_revenue}</p>
        </div>
        <div className="bg-gray-800/60 rounded-xl p-5 border border-gray-700">
          <p className="text-gray-400 text-sm mb-1">Creator Payouts</p>
          <p className="text-3xl font-bold text-amber-400">₹{overview.total_creator_payouts}</p>
        </div>
        <div className="bg-gray-800/60 rounded-xl p-5 border border-gray-700">
          <p className="text-gray-400 text-sm mb-1">Active Creators</p>
          <p className="text-3xl font-bold text-white">{overview.total_creators}</p>
        </div>
      </div>

      {/* Creators Table */}
      {overview.creators.length > 0 ? (
        <div className="bg-gray-800/40 rounded-xl border border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Creator</th>
                <th className="text-center p-4 text-gray-400 font-medium text-sm">Products</th>
                <th className="text-center p-4 text-gray-400 font-medium text-sm">Sales</th>
                <th className="text-right p-4 text-gray-400 font-medium text-sm">Revenue</th>
                <th className="text-right p-4 text-gray-400 font-medium text-sm">Creator Earnings</th>
                <th className="text-right p-4 text-gray-400 font-medium text-sm">Platform Cut</th>
              </tr>
            </thead>
            <tbody>
              {overview.creators.map(creator => (
                <tr key={creator.creator_id} className="border-b border-gray-800 hover:bg-gray-700/30">
                  <td className="p-4 text-white font-medium">{creator.creator_name}</td>
                  <td className="p-4 text-center text-gray-300">{creator.total_products}</td>
                  <td className="p-4 text-center text-gray-300">{creator.total_sales}</td>
                  <td className="p-4 text-right text-gray-300 font-mono">₹{creator.total_revenue}</td>
                  <td className="p-4 text-right text-amber-400 font-mono">₹{creator.total_creator_earnings}</td>
                  <td className="p-4 text-right text-green-400 font-mono">₹{creator.platform_earnings}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <IndianRupee className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No creator earnings data yet</p>
        </div>
      )}
    </div>
  );
};

// Payouts Tab - Admin controls all payouts
const PayoutsTab = ({ token }) => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState({});
  const [walletOverview, setWalletOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [settleModal, setSettleModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [paymentRef, setPaymentRef] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [wdRes, overviewRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/withdrawals`, {
          headers, params: { status: filter || undefined }
        }),
        axios.get(`${BACKEND_URL}/api/admin/wallet-overview`, { headers }),
      ]);
      setWithdrawals(wdRes.data.withdrawals || []);
      setStats(wdRes.data.stats || {});
      setWalletOverview(overviewRes.data.stats || {});
    } catch (err) {
      console.error('Payouts fetch error:', err);
      toast.error('Failed to load payouts data');
    } finally {
      setLoading(false);
    }
  }, [token, filter]); // eslint-disable-line

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSettle = async (wdId) => {
    setProcessing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/withdrawals/${wdId}/settle`, {
        notes: adminNotes,
        payment_reference: paymentRef,
      }, { headers });
      toast.success('Withdrawal settled successfully!');
      setSettleModal(null);
      setPaymentRef('');
      setAdminNotes('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to settle');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (wdId) => {
    setProcessing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/withdrawals/${wdId}/reject`, {
        reason: rejectReason || 'Rejected by admin',
      }, { headers });
      toast.success('Withdrawal rejected, funds returned to wallet.');
      setRejectModal(null);
      setRejectReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      processing: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      settled: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return (
      <span className={`text-xs px-2.5 py-1 rounded-full border ${map[status] || map.pending}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const ov = walletOverview || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Payout Management</h1>
          <p className="text-gray-400">Review, settle, or reject creator withdrawal requests</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Platform Wallet Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-5">
            <p className="text-xs text-gray-400 mb-1">Total in Wallets</p>
            <p className="text-2xl font-bold text-white">₹{(ov.total_balance || 0).toLocaleString('en-IN')}</p>
            <p className="text-xs text-gray-500 mt-1">{ov.wallet_count || 0} wallets</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-5">
            <p className="text-xs text-gray-400 mb-1">Tips Revenue</p>
            <p className="text-2xl font-bold text-pink-400">₹{(ov.total_tip_revenue || 0).toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-5">
            <p className="text-xs text-gray-400 mb-1">Merch Revenue</p>
            <p className="text-2xl font-bold text-green-400">₹{(ov.total_merch_revenue || 0).toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-5">
            <p className="text-xs text-gray-400 mb-1">Pending Payouts</p>
            <p className="text-2xl font-bold text-yellow-400">₹{(stats.pending_amount || 0).toLocaleString('en-IN')}</p>
            <p className="text-xs text-gray-500 mt-1">{stats.pending_count || 0} requests</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-5">
            <p className="text-xs text-gray-400 mb-1">Total Settled</p>
            <p className="text-2xl font-bold text-emerald-400">₹{(stats.total_settled || 0).toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {['pending', 'settled', 'rejected', ''].map(f => (
          <Button
            key={f || 'all'}
            size="sm"
            variant={filter === f ? 'default' : 'outline'}
            onClick={() => setFilter(f)}
            className={filter === f
              ? 'bg-purple-600 hover:bg-purple-700'
              : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
          >
            {f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
          </Button>
        ))}
      </div>

      {/* Withdrawals Table */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          ) : withdrawals.length === 0 ? (
            <div className="text-center py-16">
              <Banknote className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No {filter || ''} withdrawal requests</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left p-4 text-gray-400 font-medium text-sm">Creator</th>
                    <th className="text-right p-4 text-gray-400 font-medium text-sm">Amount</th>
                    <th className="text-center p-4 text-gray-400 font-medium text-sm">Method</th>
                    <th className="text-left p-4 text-gray-400 font-medium text-sm">Pay To</th>
                    <th className="text-center p-4 text-gray-400 font-medium text-sm">Status</th>
                    <th className="text-center p-4 text-gray-400 font-medium text-sm">Wallet ₹</th>
                    <th className="text-center p-4 text-gray-400 font-medium text-sm">Requested</th>
                    <th className="text-right p-4 text-gray-400 font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map(wd => (
                    <tr key={wd.id} className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {wd.creator_avatar ? (
                            <img src={wd.creator_avatar} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-cyan-500 flex items-center justify-center text-white text-sm font-bold">
                              {(wd.creator_display_name || wd.creator_name || '?').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="text-white font-medium text-sm">{wd.creator_display_name || wd.creator_name}</p>
                            {wd.creator_email && <p className="text-gray-500 text-xs">{wd.creator_email}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <span className="text-xl font-bold text-white">₹{wd.amount?.toLocaleString('en-IN')}</span>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full uppercase font-medium ${wd.payout_method === 'upi' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                          }`}>
                          {wd.payout_method}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <code className="text-cyan-400 text-sm bg-gray-800 px-2 py-1 rounded">{wd.payout_address}</code>
                          <button
                            onClick={() => { navigator.clipboard.writeText(wd.payout_address); toast.success('Copied!'); }}
                            className="text-gray-500 hover:text-white transition-colors"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          {wd.payout_method === 'paypal' && wd.payout_address && (
                            <a
                              href={wd.payout_address.startsWith('http') ? wd.payout_address : `https://${wd.payout_address}`}
                              target="_blank" rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-center">{statusBadge(wd.status)}</td>
                      <td className="p-4 text-center text-gray-300 text-sm font-mono">
                        ₹{(wd.wallet_balance || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-4 text-center text-gray-400 text-sm">
                        {wd.requested_at ? new Date(wd.requested_at).toLocaleDateString() : ''}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {wd.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => { setSettleModal(wd); setPaymentRef(''); setAdminNotes(''); }}
                                className="bg-green-600 hover:bg-green-700 text-white text-xs"
                              >
                                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                Settle
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setRejectModal(wd); setRejectReason(''); }}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs"
                              >
                                <XCircle className="w-3.5 h-3.5 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          {wd.status === 'settled' && (
                            <span className="text-xs text-green-400">
                              {wd.payment_reference ? `Ref: ${wd.payment_reference}` : '✓ Settled'}
                            </span>
                          )}
                          {wd.status === 'rejected' && wd.admin_notes && (
                            <span className="text-xs text-red-400 italic max-w-32 truncate" title={wd.admin_notes}>
                              {wd.admin_notes}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settle Modal */}
      {settleModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSettleModal(null)}>
          <Card className="bg-gray-900 border-gray-800 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p>Settle Withdrawal</p>
                  <p className="text-sm text-gray-400 font-normal">
                    ₹{settleModal.amount?.toLocaleString('en-IN')} → {settleModal.payout_address}
                  </p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Payout details */}
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-400">Creator</p>
                    <p className="text-white font-medium">{settleModal.creator_display_name || settleModal.creator_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Method</p>
                    <p className="text-white font-medium uppercase">{settleModal.payout_method}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-400">Pay To</p>
                    <div className="flex items-center gap-2">
                      <code className="text-cyan-400 bg-gray-900 px-2 py-1 rounded text-sm">{settleModal.payout_address}</code>
                      <button
                        onClick={() => { navigator.clipboard.writeText(settleModal.payout_address); toast.success('Copied!'); }}
                        className="text-gray-500 hover:text-white"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                <p className="text-gray-400 text-sm">Amount to Pay</p>
                <p className="text-4xl font-bold text-green-400">₹{settleModal.amount?.toLocaleString('en-IN')}</p>
              </div>

              <div>
                <p className="text-gray-400 text-sm mb-1">Payment Reference (UTR / Transaction ID)</p>
                <Input
                  value={paymentRef}
                  onChange={e => setPaymentRef(e.target.value)}
                  placeholder="e.g. UTR123456789"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <p className="text-gray-400 text-sm mb-1">Notes (optional)</p>
                <Input
                  value={adminNotes}
                  onChange={e => setAdminNotes(e.target.value)}
                  placeholder="Add a note..."
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                <p className="text-yellow-400/80 text-xs">
                  This will deduct ₹{settleModal.amount?.toLocaleString('en-IN')} from the creator's wallet balance.
                  Make sure you have already sent the payment before settling.
                </p>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => setSettleModal(null)} variant="outline" className="flex-1 border-gray-700 text-gray-300">
                  Cancel
                </Button>
                <Button
                  onClick={() => handleSettle(settleModal.id)}
                  disabled={processing}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                >
                  {processing ? 'Processing...' : 'Confirm Settlement'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setRejectModal(null)}>
          <Card className="bg-gray-900 border-gray-800 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p>Reject Withdrawal</p>
                  <p className="text-sm text-gray-400 font-normal">
                    ₹{rejectModal.amount?.toLocaleString('en-IN')} from {rejectModal.creator_display_name || rejectModal.creator_name}
                  </p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-400 text-sm">
                The locked amount will be returned to the creator's wallet balance.
              </p>

              <div>
                <p className="text-gray-400 text-sm mb-1">Rejection Reason</p>
                <Input
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Reason for rejection..."
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div className="flex gap-3">
                <Button onClick={() => setRejectModal(null)} variant="outline" className="flex-1 border-gray-700 text-gray-300">
                  Cancel
                </Button>
                <Button
                  onClick={() => handleReject(rejectModal.id)}
                  disabled={processing}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  {processing ? 'Processing...' : 'Reject & Refund'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};


// Analytics Tab
const AnalyticsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/analytics?days=${timeRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [token, timeRange]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const PIE_COLORS = ['#8B5CF6', '#06B6D4', '#F59E0B', '#10B981', '#EF4444'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  const ai = data?.ai_generations || {};
  const credits = data?.credit_economy || {};
  const modelData = Object.entries(ai.model_breakdown || {}).map(([name, count]) => ({ name: name || 'unknown', value: count }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Platform Analytics</h1>
          <p className="text-gray-400">Real-time tracking, AI metrics & engagement insights</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-900 rounded-lg p-1 border border-gray-800">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => setTimeRange(d)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  timeRange === d ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
                }`}>
                {d}d
              </button>
            ))}
          </div>
          <Button onClick={fetchAnalytics} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* External Analytics Dashboards */}
      <div>
        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">📊 Live Dashboards — Click to Open</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <a href="https://us.posthog.com" target="_blank" rel="noopener noreferrer"
            className="group p-4 rounded-xl bg-gradient-to-br from-blue-900/40 to-blue-800/20 border border-blue-800/50 hover:border-blue-500 transition-all hover:scale-[1.02] cursor-pointer block">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-blue-600/30"><BarChart3 className="w-5 h-5 text-blue-400" /></div>
              <div>
                <p className="text-white font-bold text-sm">PostHog</p>
                <p className="text-[10px] text-blue-300">✅ ACTIVE — Tracking</p>
              </div>
            </div>
            <p className="text-[11px] text-gray-400">Page views, funnels, user paths, session recordings, feature flags</p>
            <div className="mt-2 flex items-center gap-1 text-[10px] text-blue-400 group-hover:text-blue-300">
              <ExternalLink className="w-3 h-3" /> Open Dashboard →
            </div>
          </a>

          <a href="https://clarity.microsoft.com" target="_blank" rel="noopener noreferrer"
            className="group p-4 rounded-xl bg-gradient-to-br from-cyan-900/40 to-cyan-800/20 border border-cyan-800/50 hover:border-cyan-500 transition-all hover:scale-[1.02] cursor-pointer block">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-cyan-600/30"><Eye className="w-5 h-5 text-cyan-400" /></div>
              <div>
                <p className="text-white font-bold text-sm">Microsoft Clarity</p>
                <p className="text-[10px] text-cyan-300">🔥 Heatmaps + Recordings</p>
              </div>
            </div>
            <p className="text-[11px] text-gray-400">Session recordings, heatmaps, rage clicks, scroll depth, dead clicks</p>
            <div className="mt-2 flex items-center gap-1 text-[10px] text-cyan-400 group-hover:text-cyan-300">
              <ExternalLink className="w-3 h-3" /> Open Dashboard →
            </div>
          </a>

          <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer"
            className="group p-4 rounded-xl bg-gradient-to-br from-orange-900/40 to-orange-800/20 border border-orange-800/50 hover:border-orange-500 transition-all hover:scale-[1.02] cursor-pointer block">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-orange-600/30"><TrendingUp className="w-5 h-5 text-orange-400" /></div>
              <div>
                <p className="text-white font-bold text-sm">Google Analytics</p>
                <p className="text-[10px] text-orange-300">⚙️ Setup Required</p>
              </div>
            </div>
            <p className="text-[11px] text-gray-400">Traffic sources, user demographics, conversion tracking, marketing attribution</p>
            <div className="mt-2 flex items-center gap-1 text-[10px] text-orange-400 group-hover:text-orange-300">
              <ExternalLink className="w-3 h-3" /> Open Dashboard →
            </div>
          </a>

          <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer"
            className="group p-4 rounded-xl bg-gradient-to-br from-green-900/40 to-green-800/20 border border-green-800/50 hover:border-green-500 transition-all hover:scale-[1.02] cursor-pointer block">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-green-600/30"><Search className="w-5 h-5 text-green-400" /></div>
              <div>
                <p className="text-white font-bold text-sm">Search Console</p>
                <p className="text-[10px] text-green-300">🔍 SEO Insights</p>
              </div>
            </div>
            <p className="text-[11px] text-gray-400">Search rankings, indexing status, crawl errors, Core Web Vitals</p>
            <div className="mt-2 flex items-center gap-1 text-[10px] text-green-400 group-hover:text-green-300">
              <ExternalLink className="w-3 h-3" /> Open Dashboard →
            </div>
          </a>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-400 mb-1">AI Panels Generated</p>
                <p className="text-3xl font-bold text-white">{ai.total || 0}</p>
                <p className="text-xs text-gray-500 mt-1">{ai.recent || 0} in last {timeRange}d</p>
              </div>
              <div className="p-3 rounded-xl bg-purple-600/20"><Image className="w-6 h-6 text-purple-400" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-400 mb-1">Credits Used</p>
                <p className="text-3xl font-bold text-white">{credits.total_used || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Avg {credits.avg_per_user || 0}/user</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-600/20"><Sparkles className="w-6 h-6 text-amber-400" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-400 mb-1">Credits Available</p>
                <p className="text-3xl font-bold text-white">{credits.total_available || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Across all users</p>
              </div>
              <div className="p-3 rounded-xl bg-cyan-600/20"><CreditCard className="w-6 h-6 text-cyan-400" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-400 mb-1">Model Split</p>
                <p className="text-3xl font-bold text-white">{modelData.length}</p>
                <p className="text-xs text-gray-500 mt-1">Active engines</p>
              </div>
              <div className="p-3 rounded-xl bg-green-600/20"><Target className="w-6 h-6 text-green-400" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1: AI Generations + Model Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-gray-900 border-gray-800 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white">AI Generations Over Time</CardTitle>
            <CardDescription className="text-gray-400">Panels generated per day</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ai.daily || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9CA3AF" fontSize={11} tickFormatter={v => v?.slice(5) || ''} />
                  <YAxis stroke="#9CA3AF" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} labelStyle={{ color: '#9CA3AF' }} />
                  <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Panels" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Model Engine Usage</CardTitle>
            <CardDescription className="text-gray-400">Gemini vs Caravaggio</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-52 flex items-center justify-center">
              {modelData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={modelData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                      {modelData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-500 text-sm">No generation data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Engagement + Credit Purchases */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><Heart className="w-5 h-5 text-pink-500" /> Engagement Trends</CardTitle>
            <CardDescription className="text-gray-400">Likes & comments per day</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.engagement?.daily || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9CA3AF" fontSize={11} tickFormatter={v => v?.slice(5) || ''} />
                  <YAxis stroke="#9CA3AF" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} labelStyle={{ color: '#9CA3AF' }} />
                  <Area type="monotone" dataKey="likes" stroke="#EC4899" fill="#EC4899" fillOpacity={0.2} name="Likes" />
                  <Area type="monotone" dataKey="comments" stroke="#06B6D4" fill="#06B6D4" fillOpacity={0.2} name="Comments" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Credit Purchases</CardTitle>
            <CardDescription className="text-gray-400">Credits bought over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={credits.daily_purchases || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9CA3AF" fontSize={11} tickFormatter={v => v?.slice(5) || ''} />
                  <YAxis stroke="#9CA3AF" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} labelStyle={{ color: '#9CA3AF' }} />
                  <Bar dataKey="credits" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Credits" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row: Top AI Users + Top Comics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-500" /> Top AI Users</CardTitle>
            <CardDescription className="text-gray-400">By credits consumed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data?.top_ai_users || []).length > 0 ? data.top_ai_users.map((u, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-800">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-500">#{i + 1}</span>
                    <div>
                      <p className="text-white font-medium">{u.display_name || u.username}</p>
                      <p className="text-xs text-gray-400">{u.credits_remaining} credits remaining</p>
                    </div>
                  </div>
                  <p className="text-purple-400 font-bold">{u.credits_used} used</p>
                </div>
              )) : <p className="text-gray-500 text-center py-4">No AI usage data yet</p>}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><Star className="w-5 h-5 text-yellow-500" /> Most Liked Comics</CardTitle>
            <CardDescription className="text-gray-400">Top performing content</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data?.top_comics || []).length > 0 ? data.top_comics.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-800">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-500">#{i + 1}</span>
                    <div>
                      <p className="text-white font-medium">{c.title || 'Untitled'}</p>
                      <p className="text-xs text-gray-400">by {c.author || 'Unknown'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-pink-400">
                    <Heart className="w-4 h-4" />
                    <span className="font-bold">{c.likes_count}</span>
                  </div>
                </div>
              )) : <p className="text-gray-500 text-center py-4">No liked comics yet</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main Admin Dashboard Component

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [growthData, setGrowthData] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('adminToken');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, growthRes, activityRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/growth?days=14`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/activity?limit=15`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStats(statsRes.data);
      setGrowthData(growthRes.data.growth_data || []);
      setActivity(activityRes.data.activities || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to fetch dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/');  // Redirect to home page
  };

  return (
    <AdminLayout activeTab={activeTab} setActiveTab={setActiveTab} onLogout={handleLogout}>
      {activeTab === 'overview' && (
        <OverviewTab stats={stats} growthData={growthData} activity={activity} loading={loading} onRefresh={fetchData} />
      )}
      {activeTab === 'users' && <UsersTab token={token} />}
      {activeTab === 'comics' && <ComicsTab token={token} />}
      {activeTab === 'messages' && <MessagesTab token={token} />}
      {activeTab === 'posts' && <PostsTab token={token} />}
      {activeTab === 'credits' && <CreditRevenueTab token={token} />}
      {activeTab === 'revenue' && <RevenueTab token={token} stats={stats} />}
      {activeTab === 'activity' && <ActivityTab activity={activity} loading={loading} />}
      {activeTab === 'store' && <StoreTab token={token} />}
      {activeTab === 'feature-toggles' && <FeatureTogglesTab token={token} />}
      {activeTab === 'user-submissions' && <UserSubmissionsTab token={token} />}
      {activeTab === 'creator-earnings' && <CreatorEarningsTab token={token} />}
      {activeTab === 'payouts' && <PayoutsTab token={token} />}
      {activeTab === 'cavaggio' && <CavaggioTab token={token} />}
      {activeTab === 'analytics' && <AnalyticsTab token={token} />}
    </AdminLayout>
  );
};

export default AdminDashboard;
